import {
  PoliticaEntregaApiCellDto,
  PoliticaEntregaApiColumnKey,
  PoliticaEntregaApiRowDto,
  RegionPoliticaDto,
} from '../../../models/politica-entrega.dto';
import {
  POLITICA_ENTREGA_COLUMN_KEYS,
  PoliticaEntregaColumnKey,
  RegionPolitica,
} from '../../../models/politica-entrega.model';
import { PoliticaEntregaCellVm, PoliticaEntregaRowVm } from '../../../models/politica-entrega.vm';

const API_COLUMN_BY_DOMAIN_KEY: Record<PoliticaEntregaColumnKey, PoliticaEntregaApiColumnKey> = {
  col1: 'col_1',
  col2: 'col_2',
  col3: 'col_3',
  col4: 'col_4',
  col5: 'col_5',
  col6: 'col_6',
  col7: 'col_7',
  col8: 'col_8',
  col9: 'col_9',
  col10: 'col_10',
  col11: 'col_11',
  col12: 'col_12',
  col13: 'col_13',
  col14: 'col_14',
  col15: 'col_15',
  col16: 'col_16',
};

const COMUNA_ROW_KEYS = [
  'comuna',
  'Comuna',
  'COMUNA',
  'nombre_comuna',
  'NombreComuna',
  'comuna_nombre',
] as const;

const LIST_ROOT_KEYS = ['data', 'Data', 'results', 'Results', 'items', 'Items'] as const;

function asPlainString(value: unknown, fallback = ''): string {
  if (value === null || value === undefined) {
    return fallback;
  }
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }
  return fallback;
}

function asNonNegativeInteger(value: unknown, fallback = 0): number {
  const numeric = Number(value);
  return Number.isInteger(numeric) && numeric >= 0 ? numeric : fallback;
}

function normalizeRowsPayload(raw: unknown): PoliticaEntregaApiRowDto[] {
  if (Array.isArray(raw)) {
    return raw as PoliticaEntregaApiRowDto[];
  }
  if (!raw || typeof raw !== 'object') {
    return [];
  }
  const o = raw as Record<string, unknown>;
  for (const key of LIST_ROOT_KEYS) {
    const v = o[key];
    if (Array.isArray(v)) {
      return v as PoliticaEntregaApiRowDto[];
    }
  }
  return [];
}

function candidateColumnKeys(apiColumnKey: PoliticaEntregaApiColumnKey): string[] {
  const n = apiColumnKey.replace(/^col_/i, '');
  const compact = `col${n}`;
  return [apiColumnKey, compact, apiColumnKey.toUpperCase(), compact.toUpperCase()];
}

export class PoliticaEntregaMapper {
  static toRegion(dto: RegionPoliticaDto): RegionPolitica {
    return {
      codigo: asNonNegativeInteger(dto.codigo, 0),
      nombre: asPlainString(dto.nombre, ''),
    };
  }

  static toRowsVm(payload: unknown): PoliticaEntregaRowVm[] {
    const rows = normalizeRowsPayload(payload);
    return rows.map((rowDto, rowIndex) => PoliticaEntregaMapper.toRowVm(rowDto, rowIndex));
  }

  private static toRowVm(dto: PoliticaEntregaApiRowDto, rowIndex: number): PoliticaEntregaRowVm {
    const record = dto as Record<string, unknown>;
    const comuna = PoliticaEntregaMapper.pickComuna(record);
    const trackId = comuna === '' ? `row-${rowIndex}` : `${rowIndex}-${comuna}`;
    return {
      comuna,
      trackId,
      cells: POLITICA_ENTREGA_COLUMN_KEYS.map((columnKey) =>
        PoliticaEntregaMapper.toCellVm(
          PoliticaEntregaMapper.pickCellDto(record, API_COLUMN_BY_DOMAIN_KEY[columnKey]),
          comuna,
          columnKey,
          rowIndex,
        ),
      ),
    };
  }

  private static pickComuna(row: Record<string, unknown>): string {
    for (const k of COMUNA_ROW_KEYS) {
      const v = row[k];
      if (v === null || v === undefined) {
        continue;
      }
      const s = asPlainString(v, '');
      if (s !== '') {
        return s;
      }
    }
    return '';
  }

  private static pickCellDto(
    row: Record<string, unknown>,
    apiColumnKey: PoliticaEntregaApiColumnKey,
  ): PoliticaEntregaApiCellDto {
    for (const k of candidateColumnKeys(apiColumnKey)) {
      if (!Object.hasOwn(row, k)) {
        continue;
      }
      const v = row[k];
      if (v !== undefined) {
        return v as PoliticaEntregaApiCellDto;
      }
    }
    return undefined;
  }

  private static cellPayloadToTuple(cellDto: unknown): [unknown, unknown, unknown] {
    if (cellDto === null || cellDto === undefined) {
      return [undefined, undefined, undefined];
    }
    if (Array.isArray(cellDto)) {
      return [cellDto[0], cellDto[1], cellDto[2]];
    }
    if (typeof cellDto === 'object') {
      const o = cellDto as Record<string, unknown>;
      const id = o['id'] ?? o['pk'] ?? o['Id'] ?? o['ID'];
      return [
        id,
        o['horas'] ?? o['Horas'],
        o['centro'] ?? o['Centro'] ?? o['code'] ?? o['Code'],
      ];
    }
    return [undefined, undefined, undefined];
  }

  private static toCellVm(
    cellDto: PoliticaEntregaApiCellDto,
    comuna: string,
    columnKey: PoliticaEntregaColumnKey,
    rowIndex: number,
  ): PoliticaEntregaCellVm {
    const pallet = Number(columnKey.replace('col', ''));
    const tuple = PoliticaEntregaMapper.cellPayloadToTuple(cellDto);
    const id = asNonNegativeInteger(tuple[0], 0);
    const horas = id > 0 ? asPlainString(tuple[1], '') : '';
    const centro = id > 0 ? asPlainString(tuple[2], '') : '';

    return {
      id,
      horas,
      centro,
      comuna,
      pallet,
      columnKey,
      hasValue: id > 0,
      trackId: `${rowIndex}-${columnKey}`,
    };
  }
}
